// ערכת התצוגה המשותפת (11, 2026-10-13) — חלק טהור: בלי DOM ובלי globals. מקור אחד לדף המארח (ChartBlock) ול-iframe של
// בלוק app (app-doc מזריק את הקובץ כטקסט ומסיר את מילת הייצוא בתחילת שורה — לכן רק ייצוא של function / const ברמה
// העליונה, בלי ייצוא ברירת מחדל, בלי רשימת ייצוא ובלי import חוץ מ-./series.js). טיפוסים: series.d.ts. נבדק ב-app.test.ts.

/** 6 צבעים משלימים אחרי 6 צבעי הפלטה — 12 צבעים שונים לפני חזרה (בהיר / כהה). צבעי Vibe (monday), web-visual-identity. */
export const EXTRA_LIGHT = ['#ff7575', '#037f4c', '#ffcb00', '#784bd1', '#66ccff', '#bb3354'];
export const EXTRA_DARK = ['#ff7575', '#33d391', '#ffcb00', '#9d7be8', '#66ccff', '#e2445c'];
const BRAND_FALLBACK = ['#0073ea', '#00c875', '#fdab3d', '#df2f4a', '#a25ddc', '#579bfc'];
export const MUTED_LIGHT = '#A8B3C2';
export const MUTED_DARK = '#5B6576';

/** פלטה של 12: צבעי המותג (--chart-1..6, משלים מברירת המחדל אם חסר) + 6 משלימים לפי הערכה. */
export function palette(brand, dark) {
  const b = (brand || []).filter(Boolean).slice(0, 6);
  while (b.length < 6) b.push(BRAND_FALLBACK[b.length]);
  return b.concat(dark ? EXTRA_DARK : EXTRA_LIGHT);
}

/** צבע לפי דירוג: 12 צבעים; מהסדרה ה-13 הצבעים חוזרים בקו מקווקו. */
export function colorFor(rank, pal) {
  const n = Math.max(1, pal.length);
  return { color: pal[((rank % n) + n) % n], dashed: rank >= n };
}

/** צבע בשקיפות: hsl(x) → hsl(x / a); #rrggbb → rgba(). */
export function withAlpha(color, a) {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (m) {
    const n = parseInt(m[1], 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  }
  const h = /^hsl\((.+)\)$/.exec(color);
  if (h) return `hsl(${h[1]} / ${a})`;
  return color;
}

export function num(x) {
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  if (typeof x === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(x)) return Number(x);
  return null;
}

const TIME_RE = /^\d{4}-(0[1-9]|1[0-2])(-\d{2})?([T ]\d{2}:\d{2}.*)?$/;
/** ערך זמן: YYYY-MM, YYYY-MM-DD, datetime ISO, או שנה (19xx/20xx). */
export function isTimeValue(v) {
  if (typeof v === 'number') return Number.isInteger(v) && v >= 1900 && v <= 2100;
  if (typeof v !== 'string') return false;
  const s = v.trim();
  return TIME_RE.test(s) || /^(19|20)\d{2}$/.test(s);
}

/** ציר זמן (רץ משמאל לימין, כמו גרף פיננסי) — כשכל הערכים זמן, או כשתפקיד העמודה time. */
export function isTimeAxis(xs, role) {
  if (role === 'time') return true;
  const vals = (xs || []).filter((v) => v !== null && v !== undefined && v !== '');
  return vals.length > 0 && vals.every(isTimeValue);
}

const MONTH_FMT = new Intl.DateTimeFormat('he-IL', { month: 'short', year: '2-digit', timeZone: 'UTC' });
/** תווית לציר x: חודש → "ספט׳ 25", יום → 01.09; אחרת כמו שהוא. */
export function xLabel(v, time) {
  const s = v === null || v === undefined ? '' : String(v);
  if (time) {
    let m = /^(\d{4})-(\d{2})$/.exec(s);
    if (m) return MONTH_FMT.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15)));
    m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}.${m[2]}`;
  }
  return s;
}

/** שמות העמודות שבנתונים (איחוד על 200 השורות הראשונות) — שמות בלבד, לעולם לא ערכים. */
export function columnsOf(rows) {
  const out = [];
  for (const r of (rows || []).slice(0, 200)) {
    if (r && typeof r === 'object') for (const k of Object.keys(r)) if (!out.includes(k)) out.push(k);
  }
  return out;
}

/** עמודה שחסרה בנתונים → הודעה בעברית עם שם העמודה ושמות העמודות הקיימות (בלי ערכים). null = הכול בסדר / אין שורות. */
export function missingColumnsHe(rows, keys) {
  if (!rows || rows.length === 0) return null;
  const have = columnsOf(rows);
  const miss = keys.filter((k) => k && !have.includes(k));
  if (!miss.length) return null;
  const one = miss.length === 1;
  return `${one ? 'העמודה' : 'העמודות'} ${miss.map((k) => `"${k}"`).join(', ')} לא ${one ? 'נמצאה' : 'נמצאו'} בנתונים. העמודות הקיימות: ${have.join(', ')}`;
}

const keyOf = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * פורמט ארוך → רחב: שורה לכל (x, סדרה) → {xs, time, series:[{name, data}]}. כפילויות נסכמות; חסר = null.
 * xs לפי סדר ההופעה, וממוינים עולה כשהציר הוא זמן (time: כפייה; ברירת מחדל — זיהוי). הסדרות לפי סדר ההופעה.
 */
export function pivot(rows, x, seriesBy, y, time) {
  const xs = [];
  const seen = new Set();
  const names = [];
  const cells = new Map();
  for (const r of rows || []) {
    if (!r) continue;
    const xv = keyOf(r[x]);
    const sv = keyOf(r[seriesBy]);
    if (!seen.has(xv)) {
      seen.add(xv);
      xs.push(xv);
    }
    if (!cells.has(sv)) {
      cells.set(sv, new Map());
      names.push(sv);
    }
    const n = num(r[y]);
    if (n === null) continue;
    const m = cells.get(sv);
    m.set(xv, (m.get(xv) ?? 0) + n);
  }
  const isTime = time ?? isTimeAxis(xs);
  if (isTime) xs.sort();
  return { xs, time: isTime, series: names.map((name) => ({ name, data: xs.map((xv) => (cells.get(name).has(xv) ? cells.get(name).get(xv) : null)) })) };
}

/**
 * מודל לגרף קרטזי: seriesBy → pivot על y[0] (כמה y עם seriesBy — הראשון); אחרת סדרה לכל מפתח ב-y.
 * ציר זמן ממוין עולה. o.time: כפייה (true/false) או undefined = זיהוי לפי הערכים.
 */
export function seriesModel(rows, o) {
  const ys = Array.isArray(o.y) ? o.y : [o.y];
  if (o.seriesBy) return pivot(rows, o.x, o.seriesBy, ys[0], o.time);
  let rs = (rows || []).filter(Boolean);
  const time = o.time ?? isTimeAxis(rs.map((r) => r[o.x]));
  if (time) {
    rs = rs
      .map((r, i) => [r, i])
      .sort((a, b) => {
        const ka = keyOf(a[0][o.x]);
        const kb = keyOf(b[0][o.x]);
        return ka < kb ? -1 : ka > kb ? 1 : a[1] - b[1];
      })
      .map((p) => p[0]);
  }
  return { xs: rs.map((r) => keyOf(r[o.x])), time, series: ys.map((k) => ({ name: k, data: rs.map((r) => num(r[k])) })) };
}

/** מספר הסדרות בבלוק בלי לבנות מודל. */
export function seriesCount(block) {
  if (!block.seriesBy) return block.y.length;
  const s = new Set();
  for (const r of block.rows || []) if (r) s.add(keyOf(r[block.seriesBy]));
  return s.size;
}

/** חוקר סדרות (טבלה שהיא מקרא): לא עוגה, ויותר מ-6 סדרות או top/show מפורשים. עד 6 — גרף רגיל. */
export function isExplorer(block) {
  if (block.chartType === 'pie') return false;
  return seriesCount(block) > 6 || block.top !== undefined || block.show !== undefined;
}

/** סטטיסטיקות לסדרה. change = נקודה אחרונה מול הקודמת לה (שבר); null כשאין קודמת או שהיא 0. */
export function seriesStats(data) {
  let total = 0;
  let count = 0;
  let last = null;
  let prev = null;
  for (const v of data) {
    if (v === null || v === undefined) continue;
    total += v;
    count++;
    prev = last;
    last = v;
  }
  return { total, avg: count ? total / count : null, last, prev, change: last !== null && prev !== null && prev !== 0 ? (last - prev) / Math.abs(prev) : null };
}

/** דירוג לפי סה"כ יורד (שוויון — לפי הסדר המקורי). מחזיר עותקים עם total ו-rank. */
export function rankSeries(series) {
  return series
    .map((s, i) => ({ s, i, total: seriesStats(s.data).total }))
    .sort((a, b) => b.total - a.total || a.i - b.i)
    .map((e, rank) => ({ ...e.s, total: e.total, rank }));
}

/** ברירת המחדל: show (אם יש) > top > 8 המובילות כשיש יותר מ-8 > הכול. names בסדר הדירוג. */
export function initialVisible(names, o) {
  o = o || {};
  if (Array.isArray(o.show)) {
    const want = new Set(o.show.map(String));
    const vis = names.filter((n) => want.has(n));
    if (vis.length) return vis;
  }
  const max = o.max ?? 8;
  if (typeof o.top === 'number') return names.slice(0, Math.max(1, o.top));
  return names.length > max ? names.slice(0, max) : names.slice();
}

const DASHES = ['solid', 'dashed', 'dotted'];
/**
 * צבע וקו לכל סדרה (09 §1 series): group = גוון אחד לחברי הקבוצה; variant = רציף/מקווקו/מנוקד לפי סדר ההופעה;
 * color: hex, אינדקס פלטה '1'..'12', או 'muted' (אפור); dash מפורש גובר. סדרה בלי קבוצה תופסת את הצבע הבא —
 * כשהסדרות מגיעות לפי דירוג, הצבע נקבע לפי הדירוג. מעבר ל-12 צבעים — חזרה בקו מקווקו.
 */
export function seriesStyles(block, pal, muted) {
  const byKey = new Map((block.series || []).map((s) => [s.key, s]));
  const n = Math.max(1, pal.length);
  const hueOf = new Map();
  const variants = [];
  let next = 0;
  const slot = (i) => ({ color: pal[i % n] ?? '#888', paletteIndex: i % n, wrapped: i >= n });
  return block.y.map((key) => {
    const s = byKey.get(key);
    let c;
    if (s && s.color === 'muted') c = { color: muted || MUTED_LIGHT, paletteIndex: null, wrapped: false };
    else if (s && s.color && s.color.startsWith('#')) c = { color: s.color, paletteIndex: null, wrapped: false };
    else if (s && s.color) c = { ...slot(Number(s.color) - 1), wrapped: false };
    else if (s && s.group !== undefined) {
      if (!hueOf.has(s.group)) hueOf.set(s.group, next++);
      c = slot(hueOf.get(s.group));
    } else c = slot(next++);
    let dash = c.wrapped ? 'dashed' : 'solid';
    if (s && s.dash) dash = s.dash;
    else if (s && s.variant !== undefined) {
      if (!variants.includes(s.variant)) variants.push(s.variant);
      dash = DASHES[Math.min(variants.indexOf(s.variant), DASHES.length - 1)];
    }
    return { key, name: (s && s.labelHe) || key, color: c.color, paletteIndex: c.paletteIndex, dash };
  });
}

/** עוגה: יותר מ-max פרוסות → max-1 הגדולות + "אחרים". */
export function pieSlices(rows, x, y, max) {
  max = max || 8;
  const all = (rows || []).filter(Boolean).map((r) => ({ name: keyOf(r[x]), value: num(r[y]) ?? 0 }));
  if (all.length <= max) return all;
  const sorted = all.slice().sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, max - 1);
  const rest = sorted.slice(max - 1).reduce((s, e) => s + e.value, 0);
  return head.concat([{ name: 'אחרים', value: rest }]);
}

/** path של SVG לקו מגמה (viewBox 0 0 w h); null = רווח בקו. */
export function sparkPath(values, w, h, pad) {
  pad = pad ?? 2;
  const vs = (values || []).map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : null));
  const real = vs.filter((v) => v !== null);
  if (!real.length) return '';
  const min = Math.min(...real);
  const max = Math.max(...real);
  const span = max - min || 1;
  const step = vs.length > 1 ? (w - 2 * pad) / (vs.length - 1) : 0;
  const r = (n) => Math.round(n * 100) / 100;
  let d = '';
  let pen = false;
  vs.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    const px = vs.length > 1 ? pad + i * step : w / 2;
    const py = max === min ? h / 2 : pad + (1 - (v - min) / span) * (h - 2 * pad);
    d += `${pen ? 'L' : 'M'}${r(px)} ${r(py)}`;
    pen = true;
  });
  return d;
}

/** CSV עם BOM (Excel פותח עברית נכון); גרש לפני מה שנראה כנוסחה (=,+,@,- שאינו מספר) — הגנה מהזרקת נוסחאות. */
export function csv(rows, columns) {
  const cols = (columns || columnsOf(rows)).map((c) => (typeof c === 'string' ? { key: c, labelHe: c } : c));
  const cell = (v) => {
    if (v === null || v === undefined) return '';
    let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (typeof v === 'string' && (/^[=+@\t\r]/.test(s) || (s.startsWith('-') && num(s) === null))) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.map((c) => cell(c.labelHe || c.key)).join(',')];
  for (const r of rows || []) lines.push(cols.map((c) => cell(r ? r[c.key] : null)).join(','));
  return String.fromCharCode(0xfeff) + lines.join('\r\n');
}

const FINALS = { ך: 'כ', ם: 'מ', ן: 'נ', ף: 'פ', ץ: 'צ' };
/** נרמול לחיפוש: אותיות קטנות, בלי ניקוד/גרשיים, אותיות סופיות → רגילות, רווחים מכווצים. */
export function normalize(s) {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-֑ͯ-ׇ]/g, '')
    .replace(/["'`׳״]/g, '')
    .replace(/[ךםןףץ]/g, (c) => FINALS[c])
    .replace(/\s+/g, ' ')
    .trim();
}

export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** RLM בתחילת תווית: עברית ומספרים לא מתהפכים בתוך SVG. */
export const RLM = String.fromCharCode(0x200f);
/** שם סדרת העזר של קווי ההערות (לא מוצגת בטבלה ולא במקרא). */
export const MARKS_SERIES = String.fromCharCode(0x200b);

/**
 * אופציית ECharts לגרף קרטזי — משותפת לגרף הרגיל ולחוקר הסדרות.
 * m = {xs, time, series:[{name, data, color, dash?}]}; t = tokens {fg, muted, border, grid, card, pointer?, font?};
 * o = {kind:'line'|'bar'|'area', stacked?, fmt(v), axisFmt(n), legend?: bool, selected?: {name: bool}, highlight?: string[],
 *      marks?: [{x, labelHe, series?}], explorer?: bool, focus?: string|null}.
 * ציר זמן רץ משמאל לימין; קטגוריות (שמות) — מימין לשמאל (inverse).
 */
export function cartesianOption(m, t, o) {
  const font = t.font || 'Figtree, "Noto Sans Hebrew", system-ui, sans-serif';
  const kind = o.kind === 'area' ? 'line' : o.kind;
  const few = m.xs.length <= 16;
  const hl = new Set(o.highlight || []);
  const marks = (o.marks || []).filter((mk) => mk && mk.x !== undefined && mk.x !== null);
  const lineMarks = marks.filter((mk) => !mk.series);
  const pointMarks = marks.filter((mk) => mk.series);
  const styled = kind === 'line' && m.series.some((s) => s.dash && s.dash !== 'solid');
  const legendOn = !!o.legend;
  const tooltip = {
    trigger: 'axis',
    confine: true,
    backgroundColor: t.card,
    borderColor: t.border,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { fontFamily: font, color: t.fg, fontSize: 13 },
    extraCssText: 'border-radius:10px;box-shadow:0 12px 32px -12px rgba(2,8,20,.45);direction:rtl;text-align:right;',
    axisPointer:
      kind === 'bar'
        ? { type: 'shadow', shadowStyle: { color: t.pointer || 'rgba(0,0,0,.05)' } }
        : { type: 'line', lineStyle: { color: t.muted, type: 'dashed', width: 1 } },
    formatter: (ps) => {
      const arr = (Array.isArray(ps) ? ps : [ps]).filter((p) => p && p.seriesName !== MARKS_SERIES && num(p.value) !== null);
      if (!arr.length) return '';
      const head = escapeHtml(xLabel(arr[0].axisValue ?? arr[0].name, m.time));
      const items = arr.length > 1 ? arr.slice().sort((a, b) => num(b.value) - num(a.value)) : arr;
      const shown = items.slice(0, 10);
      const line = (p) =>
        `<div style="display:flex;gap:16px;justify-content:space-between;align-items:center"><span>${p.marker || ''}${escapeHtml(p.seriesName)}</span>` +
        `<b style="font-variant-numeric:tabular-nums;unicode-bidi:isolate">${escapeHtml(o.fmt(p.value))}</b></div>`;
      const more = items.length > shown.length ? `<div style="color:${t.muted};font-size:12px">ועוד ${items.length - shown.length}</div>` : '';
      return `<div style="font-weight:600;margin-block-end:4px">${head}</div>${shown.map(line).join('')}${more}`;
    },
  };
  const series = m.series.map((s) => {
    const key = hl.has(s.name);
    // focus (חוקר: מעבר על שורה/קו) — הסדרה בולטת והשאר כמעט שקופים; נשלט ע"י האופציה, לא ע"י emphasis של ECharts
    const focused = o.focus !== undefined && o.focus !== null;
    const isFocus = focused && o.focus === s.name;
    const faded = focused && !isFocus;
    const dim = !focused && hl.size > 0 && !key;
    const base = { name: s.name, type: kind, stack: o.stacked ? 'total' : undefined, data: s.data, z: isFocus ? 6 : key ? 4 : 2 };
    const pm = pointMarks.filter((mk) => mk.series === s.name);
    const markPoint = pm.length
      ? {
          symbol: 'circle',
          symbolSize: 9,
          itemStyle: { color: s.color, borderColor: t.card, borderWidth: 2 },
          label: {
            show: true,
            position: 'top',
            distance: 6,
            formatter: (p) => RLM + p.name,
            color: t.fg,
            fontFamily: font,
            fontSize: 12,
            backgroundColor: t.card,
            borderColor: t.border,
            borderWidth: 1,
            borderRadius: 6,
            padding: [2, 6],
          },
          data: pm.map((mk) => {
            const xi = m.xs.indexOf(String(mk.x));
            return { name: mk.labelHe, coord: [String(mk.x), xi >= 0 ? s.data[xi] : null] };
          }),
        }
      : undefined;
    if (kind === 'bar') {
      return {
        ...base,
        barMaxWidth: 28,
        barGap: '20%',
        // קצה מעוגל 4px רק בקצה הנתון; בערימה — רווח בצבע המשטח בין המקטעים
        itemStyle: o.stacked
          ? { color: s.color, borderColor: t.card, borderWidth: 1, opacity: faded ? 0.15 : 1 }
          : { color: s.color, borderRadius: [4, 4, 0, 0], opacity: faded ? 0.15 : dim ? 0.55 : 1 },
        emphasis: o.explorer || o.manualFocus ? { disabled: true } : { focus: 'series' },
        markPoint,
      };
    }
    return {
      ...base,
      smooth: 0.25,
      symbol: 'circle',
      symbolSize: 7,
      showSymbol: few && !o.explorer,
      triggerEvent: !!(o.explorer || o.manualFocus),
      lineStyle: { width: isFocus ? (key ? 4 : 3) : key ? 3.5 : 2, color: s.color, type: s.dash || 'solid', opacity: faded ? 0.12 : dim ? 0.7 : 1 },
      itemStyle: { color: s.color, borderColor: t.card, borderWidth: 2, opacity: faded ? 0.12 : 1 },
      emphasis: o.explorer || o.manualFocus ? { disabled: true } : { focus: 'series', scale: 1.4 },
      blur: { lineStyle: { opacity: 0.12 }, itemStyle: { opacity: 0.12 }, areaStyle: { opacity: 0.04 } },
      areaStyle:
        o.kind === 'area'
          ? {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: withAlpha(s.color, o.explorer ? 0.14 : o.stacked ? 0.45 : 0.3) },
                  { offset: 1, color: withAlpha(s.color, o.stacked ? 0.12 : 0) },
                ],
              },
            }
          : undefined,
      markPoint,
    };
  });
  if (lineMarks.length) {
    // קווים אנכיים — על סדרת עזר ריקה, כך שהם נשארים כשסדרות מוסתרות
    series.push({
      name: MARKS_SERIES,
      type: 'line',
      data: [],
      silent: true,
      showSymbol: false,
      lineStyle: { width: 0, opacity: 0 },
      tooltip: { show: false },
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: { color: t.muted, type: [4, 4], width: 1 },
        // הדגשת סדרה (מעבר על שורה) לא מעמעמת את ההערה
        blur: { lineStyle: { opacity: 1 }, label: { opacity: 1 } },
        label: { show: true, position: 'end', distance: 4, formatter: (p) => RLM + p.name, color: t.fg, fontFamily: font, fontSize: 12, backgroundColor: t.card, padding: [1, 4], borderRadius: 4 },
        data: lineMarks.map((mk) => ({ name: mk.labelHe, xAxis: String(mk.x) })),
      },
    });
  }
  const selected = { ...(o.selected || {}) };
  if (lineMarks.length) selected[MARKS_SERIES] = true;
  return {
    color: m.series.map((s) => s.color),
    textStyle: { fontFamily: font, color: t.fg },
    aria: { enabled: true },
    animationDurationUpdate: 300,
    tooltip,
    legend: {
      show: legendOn,
      type: 'plain',
      top: 0,
      data: m.series.map((s) => s.name),
      selected,
      ...(styled ? { itemWidth: 22, itemHeight: 10 } : { icon: 'roundRect', itemWidth: 12, itemHeight: 8 }),
      itemGap: 16,
      textStyle: { color: t.muted, fontFamily: font, fontSize: 13 },
    },
    grid: {
      top: (legendOn ? 40 : 14) + (lineMarks.length ? 18 : 0) + (pointMarks.length ? 16 : 0),
      bottom: 4,
      left: 4,
      right: 12,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    xAxis: {
      type: 'category',
      data: m.xs,
      // ציר זמן: משמאל לימין (כמו גרף פיננסי). קטגוריות: הראשונה מימין (RTL)
      inverse: !m.time,
      axisLabel: { color: t.muted, hideOverlap: true, fontFamily: font, fontSize: 12, margin: 10, formatter: (v) => RLM + xLabel(v, m.time) },
      axisLine: { lineStyle: { color: t.border } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      min: (v) => Math.min(0, v.min),
      axisLabel: { color: t.muted, formatter: (n) => o.axisFmt(n), fontFamily: font, fontSize: 12, margin: 12 },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: t.grid, type: [4, 4] } },
    },
    series,
  };
}
